import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { fetchGameStages } from '../../functions/stageReport';
import {
  countAssignedStages,
  mergeGameAssignmentIntoUserData,
  readStoredUserData,
} from '../../utils/gameStages';
import { getPuzzleRevealPercent, getPuzzleThemeConfig } from '../../utils/puzzleTheme';
import './StagePuzzleReveal.css';
import './stageLayout.css';

function formatRevealPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export default function StagePuzzleReveal({
  themeData,
  completedDaysOverride,
  animateReveal = false,
}) {
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);

  const [completedDays, setCompletedDays] = useState(
    Number.isFinite(Number(completedDaysOverride)) ? Number(completedDaysOverride) : null
  );
  const [puzzleId, setPuzzleId] = useState(() => {
    const stored = readStoredUserData();
    return stored.puzzle_id ?? stored.puzzleid ?? null;
  });
  const [gameAssignment, setGameAssignment] = useState(
    () => readStoredUserData()?.gameStages ?? null
  );

  const totalAssignedStages = useMemo(
    () => countAssignedStages(gameAssignment ?? readStoredUserData()),
    [gameAssignment]
  );

  const config = useMemo(
    () => getPuzzleThemeConfig(themeData, puzzleId),
    [themeData, puzzleId]
  );

  const puzzleImage = useMemo(() => {
    const slices = config.slices ?? [];
    const id = Number(puzzleId);
    if (Number.isFinite(id) && id >= 1 && slices[id - 1]) {
      return slices[id - 1];
    }
    return config.image || '';
  }, [config.slices, config.image, puzzleId]);

  const revealPercent = useMemo(
    () => getPuzzleRevealPercent(completedDays ?? 0, totalAssignedStages),
    [completedDays, totalAssignedStages]
  );

  const [displayPercent, setDisplayPercent] = useState(() =>
    animateReveal
      ? getPuzzleRevealPercent(Math.max(0, (completedDays ?? 0) - 1), totalAssignedStages)
      : revealPercent
  );

  useEffect(() => {
    if (Number.isFinite(Number(completedDaysOverride))) {
      setCompletedDays(Number(completedDaysOverride));
    }
  }, [completedDaysOverride]);

  useEffect(() => {
    if (!animateReveal) {
      setDisplayPercent(revealPercent);
      return undefined;
    }

    if (!Number.isFinite(Number(completedDays))) {
      return undefined;
    }

    const fromPercent = getPuzzleRevealPercent(
      Math.max(0, Number(completedDays) - 1),
      totalAssignedStages
    );
    const toPercent = getPuzzleRevealPercent(Number(completedDays), totalAssignedStages);

    if (fromPercent >= toPercent) {
      setDisplayPercent(toPercent);
      return undefined;
    }

    setDisplayPercent(fromPercent);

    let raf1 = 0;
    let raf2 = 0;
    let timerId = 0;

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        timerId = window.setTimeout(() => {
          setDisplayPercent(toPercent);
        }, animateReveal ? 560 : 180);
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(timerId);
    };
  }, [animateReveal, completedDays, revealPercent, totalAssignedStages]);

  useEffect(() => {
    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      if (!Number.isFinite(Number(completedDaysOverride))) setCompletedDays(0);
      return undefined;
    }

    let cancelled = false;

    fetchGameStages({ token, userId, organizationId, sessionId })
      .then((data) => {
        if (cancelled) return;

        const game = data?.game ?? null;
        const id = game?.puzzle_id ?? readStoredUserData().puzzle_id ?? null;
        if (id != null) setPuzzleId(id);

        if (game) {
          setGameAssignment(game);
          mergeGameAssignmentIntoUserData({
            game,
            currentStage: data?.currentStage,
            nextDay: data?.nextDay,
          });
        }

        if (Number.isFinite(Number(completedDaysOverride))) {
          setCompletedDays(Number(completedDaysOverride));
          return;
        }

        const nextDay = data?.nextDay != null ? Number(data.nextDay) : 1;
        const total = countAssignedStages(game);
        const done = Number.isFinite(nextDay)
          ? Math.max(0, Math.min(total, nextDay - 1))
          : 0;
        setCompletedDays(done);
      })
      .catch(() => {
        if (!cancelled && !Number.isFinite(Number(completedDaysOverride))) {
          setCompletedDays(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [adminToken, completedDaysOverride, user?.organizationId, user?.sessionId, user?.userId]);

  if (!puzzleImage) {
    return (
      <aside className="stage-puzzle-reveal" aria-label="Puzzle preview">
        <p className="stage-puzzle-reveal-empty">Puzzle preview unavailable</p>
      </aside>
    );
  }

  const maskClip = `inset(0 0 0 ${displayPercent}%)`;

  return (
    <aside className="stage-puzzle-reveal" aria-label="Puzzle preview">
      <div className="stage-puzzle-reveal-card">
        <h2 className="stage-puzzle-reveal-title">What you&apos;re building</h2>
        <div className="stage-puzzle-reveal-frame">
          <img
            src={puzzleImage}
            alt="Puzzle preview"
            className="stage-puzzle-reveal-image"
          />
          <div
            className={`stage-puzzle-reveal-mask${
              animateReveal ? ' stage-puzzle-reveal-mask--animate' : ''
            }`}
            style={{ clipPath: maskClip }}
            aria-hidden
          />
        </div>
        <p className="stage-puzzle-reveal-progress">
          {formatRevealPercent(displayPercent)}% revealed
        </p>
      </div>
    </aside>
  );
}
